import type { Locale, StandardField } from './types';

const zh = {
  appName: 'BOQ Lint',
  brandDescriptor: '清单校核',
  skipToContent: '跳到主要内容',
  openSettings: '打开规则设置',
  closeSettings: '关闭规则设置',
  language: '语言',
  switchToEnglish: '切换到英文',
  switchToChinese: '切换到简体中文',
  theme: '主题',
  themeAuto: '跟随系统',
  themeLight: '浅色',
  themeDark: '深色',
  stepNavigation: '检查进度',
  stepImport: '导入',
  stepMapping: '映射',
  stepCheck: '检查',
  stepResults: '结果',
  currentStep: '当前步骤',
  completedStep: '已完成',
  importTitle: '交付前，先把清单查一遍',
  importIntro: '定位字段缺失、重复项、金额计算、单位冲突和公式问题。',
  privacyShort: '文件仅在本机浏览器中处理，不会上传',
  privacyDetail: '不连接服务器，不修改原工作簿。关闭或清除后，工作簿数据会从当前页面内存中移除。',
  workflowTitle: '使用流程',
  workflowText: '选择检查模式，导入工作簿，确认字段映射，然后查看并导出独立检查报告。',
  modeLegend: '选择检查模式',
  modeRequired: '必需字段',
  unpricedTitle: '未计价工程量清单',
  unpricedDescription: '检查编码、名称、单位和工程量，不要求综合单价与合价。',
  unpricedFields: '项目编码、项目名称、计量单位、工程量',
  pricedTitle: '已计价工程量清单',
  pricedDescription: '同时检查综合单价、合价，以及工程量乘综合单价的计算结果。',
  pricedFields: '项目编码、项目名称、计量单位、工程量、综合单价、合价',
  uploadTitle: '导入 .xlsx 工作簿',
  uploadDescription: '将文件拖到这里，或从设备中选择文件。',
  uploadActive: '松开即可读取工作簿',
  chooseFile: '选择 .xlsx 文件',
  fileInputLabel: '选择工程量清单 xlsx 文件',
  supportedFormat: '当前支持不超过 20 MB 的 .xlsx。加密、损坏或旧版 .xls 文件会被拒绝。',
  sampleTitle: '没有文件？使用虚构演示数据',
  sampleDescription: '示例会真正进入映射和检查流程，数据不代表真实项目或价格。',
  sampleValid: '加载正常清单',
  sampleIssues: '加载含问题清单',
  loadingFile: '正在读取工作簿',
  loadedFile: '工作簿已就绪',
  fileName: '文件名',
  fileSize: '文件大小',
  sheetCount: '工作表',
  sheetsUnit: '张',
  fileSourceSample: '内置示例',
  fileSourceLocal: '本地文件',
  clearFile: '清除文件与结果',
  continueMapping: '继续识别与映射',
  importErrorTitle: '无法读取这个文件',
  errorXls: '暂不支持旧版 .xls 文件。请先在表格软件中另存为 .xlsx。',
  errorUnsupported: '仅支持 .xlsx 文件，暂不支持 .xls、.xlsm、PDF 或其他格式。',
  errorEncrypted: '无法读取加密工作簿。请移除打开密码后重新导入。',
  errorCorrupt: '工作簿已损坏或不是有效的 .xlsx 文件，请检查文件后重试。',
  errorEmptyWorkbook: '工作簿中没有可读取的工作表。',
  errorNoVisibleSheet: '工作簿中没有可见工作表。请先取消隐藏至少一张工作表。',
  errorHeader: '无法识别表头。请返回映射步骤，手动选择表头行并完成字段映射。',
  errorNoDetails: '所选工作表中没有可检查的明细行。请确认表头行、字段映射和工作表选择。',
  errorInvalidConfig: '规则配置无效。请恢复默认设置或重新导入有效配置。',
  errorTooLarge: '文件超过 20 MB 限制。请精简工作簿后重试。',
  errorGeneric: '读取失败。请确认文件未加密、格式正确，然后重试。',
  mappingTitle: '确认工作表和字段映射',
  mappingIntro: '系统在每张表前 50 行中识别表头。请确认要检查的表，并修正识别结果。',
  selectedSheets: '已选择工作表',
  selectAllSheets: '选择全部可见工作表',
  clearSheetSelection: '取消全部选择',
  hiddenSheet: '隐藏',
  veryHiddenSheet: '深度隐藏',
  visibleSheet: '可见',
  rowsAndColumns: '{rows} 行，{columns} 列',
  headerRow: '表头行',
  headerDetected: '自动识别',
  headerLowConfidence: '识别把握较低，请人工确认',
  headerManualHint: '输入 1 到 50 之间的 Excel 行号。',
  fieldMapping: '字段映射',
  standardField: '标准字段',
  workbookColumn: '工作簿列',
  notMapped: '不映射',
  requiredField: '必需',
  optionalField: '可选',
  previewTitle: '数据预览',
  previewDescription: '显示表头后的前 10 行。行号与 Excel 保持一致。',
  emptyPreview: '此表头后没有可预览的数据。',
  noSheetSelected: '请至少选择一张可见工作表。',
  mappingMissing: '所选工作表仍缺少必需字段映射。',
  mappingReady: '字段映射可以开始检查。',
  backToImport: '返回导入',
  startCheck: '开始检查',
  checkingTitle: '正在检查工作簿',
  checkingIntro: '大批量数据会分段处理。请保持此页面打开。',
  checkErrorTitle: '检查未能完成',
  phaseParsing: '读取工作簿结构',
  phaseMapping: '应用字段映射',
  phaseChecking: '执行规则检查',
  phaseReporting: '整理检查结果',
  progressComplete: '检查完成',
  cancelNotAvailable: '检查完成前无需操作',
  resultTitle: '检查结果',
  resultIntro: '问题精确到工作表、Excel 行号和字段。请由专业人员复核后修正原工作簿。',
  resultPassTitle: '未发现已启用规则所定义的问题',
  resultPassText: '这表示本次通用数据质量检查已完成，不代表符合任何国家、行业或地方标准。',
  totalRows: '已检查清单项',
  sheetsChecked: '已检查工作表',
  severeIssues: '严重问题',
  errors: '错误',
  warnings: '警告',
  infos: '提示',
  passedRules: '已通过规则',
  duration: '检查耗时',
  milliseconds: '{value} 毫秒',
  seconds: '{value} 秒',
  issueList: '问题明细',
  issueCount: '共 {count} 条问题',
  filterSeverity: '严重程度',
  filterRule: '规则编号',
  filterSheet: '工作表',
  filterAll: '全部',
  searchLabel: '搜索问题',
  searchPlaceholder: '搜索项目编码、名称或问题说明',
  clearFilters: '清除筛选',
  noFilteredIssues: '没有符合当前筛选条件的问题。',
  severity: '严重程度',
  ruleId: '规则编号',
  worksheet: '工作表',
  excelRow: 'Excel 行号',
  fieldColumn: '字段/列',
  itemCode: '项目编码',
  itemName: '项目名称',
  originalValue: '原始值',
  issueDescription: '问题说明',
  fixSuggestion: '修复建议',
  rowLevel: '整行',
  notAvailable: '无',
  calculatedValue: '计算合价',
  difference: '差额',
  openContext: '查看第 {row} 行上下文',
  contextTitle: '原始行上下文',
  contextDescription: '{sheet} 工作表第 {row} 行及相邻行',
  issueRow: '问题行',
  closeContext: '关闭行上下文',
  rowsPerPage: '每页条数',
  pagination: '结果分页',
  pageStatus: '第 {page} 页，共 {pages} 页',
  previousPage: '上一页',
  nextPage: '下一页',
  exportTitle: '导出独立检查报告',
  exportDescription: '报告不会修改原工作簿。CSV 使用 UTF-8 BOM，JSON 不包含工作簿对象。',
  exportCsv: '导出 CSV',
  exportJson: '导出 JSON',
  exportXlsx: '导出 XLSX 报告',
  exporting: '正在生成报告',
  exportError: '报告生成失败，请稍后重试。',
  backToMapping: '返回修改映射',
  recheck: '重新检查',
  newFile: '清除并检查新文件',
  settingsTitle: '规则设置',
  settingsIntro: '14 条规则默认全部启用，可按本次交付要求逐条调整；结果页修改后会立即重新检查。',
  toleranceLabel: 'QG010 绝对允许误差',
  toleranceUnit: '元',
  toleranceHint: '请输入大于或等于 0 的十进制数，默认值为 0.01。',
  relativeToleranceLabel: 'QG010 相对允许误差',
  relativeToleranceHint:
    '输入比例值，默认 0.001（即 0.1%）。实际允许误差取绝对值与相对值中的较大者。',
  dispersionRatioLabel: 'QG012 单价偏离比例',
  dispersionRatioHint: '输入相对中位数的偏离比例，默认 0.5（即 50%）。',
  featureMinLengthLabel: 'QG009 特征最短长度',
  featureMinLengthHint: '去除空白和标点后的最短长度，默认 6。',
  ratioUnit: '比例',
  characterUnit: '字',
  ruleEnabled: '启用',
  coreRule: '默认规则',
  ruleDescription: '规则说明',
  settingsSaved: '设置已保存在此浏览器中。不会保存文件名、工作簿或检查结果。',
  restoreDefaults: '恢复默认设置',
  importConfig: '导入 JSON 配置',
  exportConfig: '导出 JSON 配置',
  configError: '配置无效，未应用任何更改。请检查文件结构和字段值。',
  configImported: '配置已导入。',
  defaultRestored: '已恢复默认设置。',
  footerBoundary:
    'BOQ Lint 仅用于工程量清单 Excel 数据质量辅助检查，不构成造价审核结论，也不能替代图纸、合同、计量规则及专业人员复核。',
  socialTitle: '把问题定位到工作表、行号和字段',
  socialSubtitle: '本地检查工程量清单 Excel，文件不上传',
  socialIssues: '问题清单示例',
  socialPrivacy: '浏览器本地处理',
  socialExport: 'CSV / JSON / XLSX 报告',
  field_serial_no: '序号',
  field_item_code: '项目编码',
  field_item_name: '项目名称',
  field_item_feature: '项目特征',
  field_unit: '计量单位',
  field_quantity: '工程量',
  field_unit_price: '综合单价',
  field_total_price: '合价',
  field_remarks: '备注',
} as const;

type MessageKey = keyof typeof zh;

const en: Record<MessageKey, string> = {
  appName: 'BOQ Lint',
  brandDescriptor: 'BOQ Validation',
  skipToContent: 'Skip to main content',
  openSettings: 'Open rule settings',
  closeSettings: 'Close rule settings',
  language: 'Language',
  switchToEnglish: 'Switch to English',
  switchToChinese: 'Switch to Simplified Chinese',
  theme: 'Theme',
  themeAuto: 'Use system theme',
  themeLight: 'Light',
  themeDark: 'Dark',
  stepNavigation: 'Check progress',
  stepImport: 'Import',
  stepMapping: 'Mapping',
  stepCheck: 'Check',
  stepResults: 'Results',
  currentStep: 'Current step',
  completedStep: 'Completed',
  importTitle: 'Check your BOQ before delivery',
  importIntro:
    'Find missing fields, duplicates, calculation differences, unit conflicts, and formula errors.',
  privacyShort: 'Your file stays in this browser and is never uploaded',
  privacyDetail:
    'No server connection and no original-workbook changes. Clear or close the page to remove workbook data from memory.',
  workflowTitle: 'How it works',
  workflowText:
    'Choose a mode, import a workbook, confirm the field mapping, then review and export an independent report.',
  modeLegend: 'Choose a check mode',
  modeRequired: 'Required fields',
  unpricedTitle: 'Unpriced BOQ',
  unpricedDescription:
    'Checks code, name, unit, and quantity. Unit price and total price are not required.',
  unpricedFields: 'Item code, item name, unit, quantity',
  pricedTitle: 'Priced BOQ',
  pricedDescription:
    'Also checks unit price, total price, and the quantity multiplied by unit price calculation.',
  pricedFields: 'Item code, item name, unit, quantity, unit price, total price',
  uploadTitle: 'Import an .xlsx workbook',
  uploadDescription: 'Drop a file here or choose one from your device.',
  uploadActive: 'Release to read the workbook',
  chooseFile: 'Choose .xlsx file',
  fileInputLabel: 'Choose a BOQ xlsx file',
  supportedFormat:
    'Supports .xlsx files up to 20 MB. Encrypted, damaged, and legacy .xls files are rejected.',
  sampleTitle: 'No file? Use fictional demo data',
  sampleDescription:
    'Samples enter the real mapping and check flow. They do not represent a real project or real prices.',
  sampleValid: 'Load valid sample',
  sampleIssues: 'Load issues sample',
  loadingFile: 'Reading workbook',
  loadedFile: 'Workbook ready',
  fileName: 'File name',
  fileSize: 'File size',
  sheetCount: 'Worksheets',
  sheetsUnit: '',
  fileSourceSample: 'Built-in sample',
  fileSourceLocal: 'Local file',
  clearFile: 'Clear file and results',
  continueMapping: 'Continue to mapping',
  importErrorTitle: 'This file could not be read',
  errorXls:
    'Legacy .xls files are not supported. Save the file as .xlsx in your spreadsheet app and try again.',
  errorUnsupported:
    'Only .xlsx files are supported. .xls, .xlsm, PDF, and other formats are not supported.',
  errorEncrypted:
    'Encrypted workbooks cannot be read. Remove the open password and import the file again.',
  errorCorrupt:
    'The workbook is damaged or is not a valid .xlsx file. Check the file and try again.',
  errorEmptyWorkbook: 'The workbook does not contain a readable worksheet.',
  errorNoVisibleSheet:
    'The workbook has no visible worksheets. Unhide at least one worksheet first.',
  errorHeader:
    'No header could be identified. Return to mapping, select a header row, and complete the field mapping.',
  errorNoDetails:
    'The selected worksheets contain no checkable detail rows. Confirm the header, mapping, and worksheet selection.',
  errorInvalidConfig: 'The rule config is invalid. Restore defaults or import a valid config.',
  errorTooLarge: 'The file exceeds the 20 MB limit. Reduce the workbook and try again.',
  errorGeneric:
    'Reading failed. Confirm that the file is not encrypted and is a valid .xlsx file, then try again.',
  mappingTitle: 'Confirm worksheets and field mapping',
  mappingIntro:
    'Headers are detected within the first 50 rows of each sheet. Confirm the sheets and correct the detected mapping.',
  selectedSheets: 'Selected worksheets',
  selectAllSheets: 'Select all visible worksheets',
  clearSheetSelection: 'Clear worksheet selection',
  hiddenSheet: 'Hidden',
  veryHiddenSheet: 'Very hidden',
  visibleSheet: 'Visible',
  rowsAndColumns: '{rows} rows, {columns} columns',
  headerRow: 'Header row',
  headerDetected: 'Auto-detected',
  headerLowConfidence: 'Low confidence. Please confirm manually.',
  headerManualHint: 'Enter an Excel row number from 1 to 50.',
  fieldMapping: 'Field mapping',
  standardField: 'Standard field',
  workbookColumn: 'Workbook column',
  notMapped: 'Not mapped',
  requiredField: 'Required',
  optionalField: 'Optional',
  previewTitle: 'Data preview',
  previewDescription: 'Shows the first 10 rows after the header. Row numbers match Excel.',
  emptyPreview: 'There is no data to preview after this header.',
  noSheetSelected: 'Select at least one visible worksheet.',
  mappingMissing: 'A selected worksheet is missing a required field mapping.',
  mappingReady: 'The field mapping is ready for checking.',
  backToImport: 'Back to import',
  startCheck: 'Start check',
  checkingTitle: 'Checking workbook',
  checkingIntro: 'Large datasets are processed in batches. Keep this page open.',
  checkErrorTitle: 'The check could not be completed',
  phaseParsing: 'Reading workbook structure',
  phaseMapping: 'Applying field mapping',
  phaseChecking: 'Running rule checks',
  phaseReporting: 'Preparing check results',
  progressComplete: 'Check complete',
  cancelNotAvailable: 'No action is needed until the check finishes',
  resultTitle: 'Check results',
  resultIntro:
    'Each issue includes a worksheet, Excel row, and field. Review and correct the original workbook professionally.',
  resultPassTitle: 'No issues were found by the enabled rules',
  resultPassText:
    'This completes a general data-quality check. It does not represent compliance with any national, industry, or local standard.',
  totalRows: 'BOQ items checked',
  sheetsChecked: 'Worksheets checked',
  severeIssues: 'Critical issues',
  errors: 'Errors',
  warnings: 'Warnings',
  infos: 'Notices',
  passedRules: 'Rules passed',
  duration: 'Check duration',
  milliseconds: '{value} ms',
  seconds: '{value} s',
  issueList: 'Issue details',
  issueCount: '{count} issues',
  filterSeverity: 'Severity',
  filterRule: 'Rule ID',
  filterSheet: 'Worksheet',
  filterAll: 'All',
  searchLabel: 'Search issues',
  searchPlaceholder: 'Search item code, name, or issue description',
  clearFilters: 'Clear filters',
  noFilteredIssues: 'No issues match the current filters.',
  severity: 'Severity',
  ruleId: 'Rule ID',
  worksheet: 'Worksheet',
  excelRow: 'Excel row',
  fieldColumn: 'Field / column',
  itemCode: 'Item code',
  itemName: 'Item name',
  originalValue: 'Original value',
  issueDescription: 'Issue description',
  fixSuggestion: 'Suggested fix',
  rowLevel: 'Whole row',
  notAvailable: 'None',
  calculatedValue: 'Calculated total',
  difference: 'Difference',
  openContext: 'View context for row {row}',
  contextTitle: 'Original row context',
  contextDescription: '{sheet}, row {row} and nearby rows',
  issueRow: 'Issue row',
  closeContext: 'Close row context',
  rowsPerPage: 'Rows per page',
  pagination: 'Result pagination',
  pageStatus: 'Page {page} of {pages}',
  previousPage: 'Previous page',
  nextPage: 'Next page',
  exportTitle: 'Export an independent check report',
  exportDescription:
    'Reports never modify the original workbook. CSV uses UTF-8 BOM, and JSON excludes workbook objects.',
  exportCsv: 'Export CSV',
  exportJson: 'Export JSON',
  exportXlsx: 'Export XLSX report',
  exporting: 'Generating report',
  exportError: 'The report could not be generated. Try again.',
  backToMapping: 'Back to mapping',
  recheck: 'Run check again',
  newFile: 'Clear and check a new file',
  settingsTitle: 'Rule settings',
  settingsIntro:
    'All 14 rules are enabled by default and can be adjusted individually. Changes on the results page trigger an immediate recheck.',
  toleranceLabel: 'QG010 absolute tolerance',
  toleranceUnit: 'CNY',
  toleranceHint: 'Enter a decimal number equal to or greater than 0. The default is 0.01.',
  relativeToleranceLabel: 'QG010 relative tolerance',
  relativeToleranceHint:
    'Enter a ratio. The default is 0.001 (0.1%). The larger absolute or relative tolerance is used.',
  dispersionRatioLabel: 'QG012 price deviation ratio',
  dispersionRatioHint: 'Enter the deviation ratio from the median. The default is 0.5 (50%).',
  featureMinLengthLabel: 'QG009 minimum feature length',
  featureMinLengthHint:
    'Minimum length after whitespace and punctuation are removed. The default is 6.',
  ratioUnit: 'ratio',
  characterUnit: 'chars',
  ruleEnabled: 'Enabled',
  coreRule: 'Default rule',
  ruleDescription: 'Rule description',
  settingsSaved:
    'Settings are stored in this browser. File names, workbooks, and results are never saved.',
  restoreDefaults: 'Restore defaults',
  importConfig: 'Import JSON config',
  exportConfig: 'Export JSON config',
  configError: 'The config is invalid. No changes were applied. Check its structure and values.',
  configImported: 'Config imported.',
  defaultRestored: 'Default settings restored.',
  footerBoundary:
    'BOQ Lint only assists with data-quality checks for Bill of Quantities Excel files. It is not a cost-audit conclusion and cannot replace drawings, contracts, measurement rules, or professional review.',
  socialTitle: 'Locate every issue by sheet, row, and field',
  socialSubtitle: 'Check BOQ Excel files locally. Files are never uploaded.',
  socialIssues: 'Issues sample',
  socialPrivacy: 'Local browser processing',
  socialExport: 'CSV / JSON / XLSX reports',
  field_serial_no: 'Serial number',
  field_item_code: 'Item code',
  field_item_name: 'Item name',
  field_item_feature: 'Item feature',
  field_unit: 'Unit',
  field_quantity: 'Quantity',
  field_unit_price: 'Unit price',
  field_total_price: 'Total price',
  field_remarks: 'Remarks',
};

export function getMessages(locale: Locale): Record<MessageKey, string> {
  return locale === 'zh-CN' ? zh : en;
}

export function formatMessage(
  messages: Record<MessageKey, string>,
  key: MessageKey,
  values: Readonly<Record<string, string | number>> = {},
): string {
  return Object.entries(values).reduce(
    (text, [name, value]) => text.replaceAll(`{${name}}`, String(value)),
    messages[key],
  );
}

export function getFieldLabel(messages: Record<MessageKey, string>, field: StandardField): string {
  return messages[`field_${field}`];
}

export type Messages = ReturnType<typeof getMessages>;
